import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./primitives";
import { Blueprint, Contours } from "./Graphics";
import { PROBLEM_QUESTIONS } from "./content";

const EASE = [0.16, 1, 0.3, 1];

export default function ProblemSection() {
  const listRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: listRef,
    offset: ["start 70%", "end 70%"],
  });
  const railScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      data-testid="problem-section"
      className="relative overflow-hidden bg-warm py-28 md:py-40"
    >
      {/* Decorative topographic motif */}
      <Contours className="pointer-events-none absolute -left-16 top-24 h-72 w-72 text-forest/15 md:h-[30rem] md:w-[30rem]" />

      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <Overline className="text-copper">Before A Single Stone Is Laid</Overline>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-x-16 md:mt-24 md:grid-cols-12">
          {/* Questions with scroll progress rail */}
          <div className="relative md:col-span-7 md:pl-10">
            {/* Rail */}
            <div className="absolute left-0 top-2 hidden h-full w-px bg-ink/10 md:block">
              <motion.div
                style={{ scaleY: railScale }}
                className="absolute left-0 top-0 h-full w-px origin-top bg-forest"
              />
            </div>

            <div ref={listRef}>
              {PROBLEM_QUESTIONS.map((q, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.6 }}
                  transition={{ duration: 1, ease: EASE }}
                  className="flex items-start gap-5 border-t border-ink/10 py-8 md:gap-7 md:py-11"
                >
                  <span className="mt-3 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-copper/50 font-serif text-sm text-copper md:mt-4">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-serif text-3xl leading-tight text-ink sm:text-5xl lg:text-6xl">
                    {q}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Blueprint graphic */}
          <div className="relative hidden md:col-span-5 md:block">
            <div className="sticky top-32">
              <Blueprint className="h-auto w-full text-forest/70" />
              <p className="mt-6 max-w-[26ch] font-serif text-xl italic text-ink/50">
                Every answer is engineered into the plan — long before the first stone is laid.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
